import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DeviceProperty } from '../../../../model/device-property';
import { PropertyType } from '../../../../model/property-type';
import { Tenant } from '../../../../model/tenant';
import { TenantsService } from '../../../../services/tenants.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { translate } from '@ngneat/transloco';
import { Device } from '../../../../model/device';
import { ActionButton } from '../../../../model/ui/action-button';
import { DevicesService } from '../../../../services/devices.service';
import { noSpecialCharacters } from '../../../../shared/custom-validators';
import { PreviousRouteService } from '../../../../services/previous-route.service';
import {DefaultPropertyType} from '../../../../model/default-property-type';

@Component({
  selector: 'app-devices-form-properties',
  templateUrl: './devices-form-properties.component.html',
  styleUrls: ['./devices-form-properties.component.scss']
})
export class DevicesFormPropertiesComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() deviceProperties: DeviceProperty[];
  @Output() devicePropertiesChange = new EventEmitter<DeviceProperty[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  devicePropertiesForm: FormGroup;
  rowsForm: FormArray;

  propertyTypes: PropertyType[];

  displayCopyFromDevice: boolean;
  copyFromDeviceActions: ActionButton[];

  copyDeviceProperties: DeviceProperty[];

  constructor(private tenantsService: TenantsService,
              private devicesService: DevicesService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService) {}

  ngOnInit(): void {
    this.buildCopyFromDeviceActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantPropertyTypes(this.tenant.uuid).toPromise().then(response => {
      this.propertyTypes = response.propertyTypes
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.createForm(this.deviceProperties);
      this.copyDeviceProperties = this.deviceProperties;
    });
  }

  private createForm(deviceProperties: DeviceProperty[]): void {
    this.rowsForm = this.formBuilder.array(
      deviceProperties.map(property => this.formGroupFromDeviceProperty(property))
    );
    this.devicePropertiesForm = this.formBuilder.group({
      deviceProperties: this.rowsForm
    });
  }

  addProperty(): void {
    const newProperty = this.formGroupFromDeviceProperty(new DeviceProperty());
    this.rowsForm.push(newProperty);
  }

  isDefault(rowIndex: number): boolean {
    return this.rowsForm.value[rowIndex].propertyType instanceof DefaultPropertyType;
  }

  getPropertyTypes(rowIndex: number): (PropertyType | DefaultPropertyType)[] {
    if (this.isDefault(rowIndex)) {
      return [this.rowsForm.value[rowIndex].propertyType];
    } else {
      return this.propertyTypes;
    }
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  onPreviousStep(): void {
    const properties = this.devicePropertiesForm.get('deviceProperties').value;
    this.previousStep.emit();
    this.devicePropertiesChange.emit(properties);
  }

  onNextStep(): void {
    if (this.devicePropertiesForm.valid) {
      const properties = this.devicePropertiesForm.get('deviceProperties').value;
      properties.forEach(property => {
        if (property.uuid == null) {
          property.uuid = this.copyDeviceProperties.find(copyProperty => copyProperty.name === property.name)?.uuid;
        }
      });
      this.nextStep.emit();
      this.devicePropertiesChange.emit(properties);
    }
  }

  private formGroupFromDeviceProperty(property: DeviceProperty): FormGroup {
    return this.formBuilder.group({
      name: [ property.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      value: [ property.value],
      propertyType: [ property.propertyType, [Validators.required] ],
      uuid: [ property.uuid ]
    });
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromDeviceActions(): void {
    this.copyFromDeviceActions = [
      {
        label: translate('misc.copyProperties'),
        icon: 'pi pi-copy',
        action: (device: Device) => this.copyProperties(device)
      }, {
        label: translate('misc.appendProperties'),
        icon: 'pi pi-plus',
        action: (device: Device) => this.copyProperties(device, true)
      }
    ];
  }

  copyProperties(device: Device, append: boolean = false): void {
    this.devicesService.getDeviceProperties(device.uuid).subscribe(response => {
      if (!append) {
        this.deleteAllRows();
      }
      const properties = this.devicePropertiesForm.get('deviceProperties').value;
      response.deviceProperties.forEach((property: DeviceProperty) => {
        property.uuid = this.copyDeviceProperties.find(copyProperty => copyProperty.name === property.name)?.uuid;
        if (properties.find(prop => prop.name === property.name) === undefined) {
          this.rowsForm.push(this.formGroupFromDeviceProperty(property));
        }
      });
      this.displayCopyFromDevice = false;
    });
  }

}
